/**
 * React Query hooks for Self-Service Profile Management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchProfile, updateProfile, changePassword } from '@/lib/users-api'
import { isApiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import type { UpdateProfileRequest, ChangePasswordRequest } from '@/types/user-management'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch the current user's full profile from the API
 */
export function useProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: fetchProfile,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Update the current user's display name or avatar
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore((state) => state.setUser)
  const user = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateProfile(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(profileKeys.me(), updated)

      // Also update the auth store so the sidebar reflects changes immediately
      if (user) {
        setUser({
          ...user,
          displayName: updated.displayName,
          avatarUrl: updated.avatarUrl ?? undefined,
        })
      }

      toast.success('Profile updated successfully')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to update profile')
      } else {
        toast.error('Failed to update profile')
      }
    },
  })
}

/**
 * Change the current user's password
 */
export function useChangePassword() {
  const setUser = useAuthStore((state) => state.setUser)
  const user = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => changePassword(data),
    onSuccess: () => {
      // Clear the mustChangePassword flag in the auth store
      if (user) {
        setUser({ ...user, mustChangePassword: false })
      }
      toast.success('Password changed successfully')
    },
    onError: (error) => {
      if (isApiError(error)) {
        if (error.code === 'INVALID_CURRENT_PASSWORD') {
          toast.error('Current password is incorrect')
        } else {
          toast.error(error.message || 'Failed to change password')
        }
      } else {
        toast.error('Failed to change password')
      }
    },
  })
}
